import {useMemo} from 'react'
import DatePicker, {registerLocale} from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {ptBR} from 'date-fns/locale/pt-BR'
import dayjs from 'dayjs'
import {
  buildTaskDateTime,
  parseTaskDateTime,
  toDatePickerValue,
} from './TaskDateTime'

registerLocale('pt-BR', ptBR)

const HOURS_24 = Array.from({length: 24}, (_, index) => String(index).padStart(2, '0'))
const MINUTES = Array.from({length: 60}, (_, index) => String(index).padStart(2, '0'))

type TaskTimeInput24hProps = {
  value: string | null
  onChange: (time: string | null) => void
  disabled?: boolean
}

function TaskTimeInput24h({value, onChange, disabled = false}: TaskTimeInput24hProps) {
  const [hour = '', minute = ''] = (value ?? '').split(':')

  const emitChange = (nextHour: string, nextMinute: string) => {
    if (!nextHour || !nextMinute) {
      onChange(null)
      return
    }

    onChange(`${nextHour}:${nextMinute}`)
  }

  return (
    <div className='d-flex align-items-center gap-2'>
      <select
        className='form-select form-select-solid py-2 rounded fw-normal'
        value={hour}
        disabled={disabled}
        aria-label='Hora'
        onChange={(event) => emitChange(event.target.value, minute || '00')}
      >
        <option value=''>--</option>
        {HOURS_24.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className='text-muted fw-semibold'>:</span>
      <select
        className='form-select form-select-solid py-2 rounded fw-normal'
        value={minute}
        disabled={disabled}
        aria-label='Minuto'
        onChange={(event) => emitChange(hour || '00', event.target.value)}
      >
        <option value=''>--</option>
        {MINUTES.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  )
}

type TaskDateTimeFieldsProps = {
  beginDateTime?: string | null
  expectedDateTime?: string | null
  onBeginDateTimeChange: (value: string | null) => void
  onExpectedDateTimeChange: (value: string | null) => void
  showExpectedDate?: boolean
  disabled?: boolean
  beginPlaceholder?: string
  expectedPlaceholder?: string
  minBeginDate?: Date
  minExpectedDate?: Date
}

export function TaskDateTimeFields({
  beginDateTime,
  expectedDateTime,
  onBeginDateTimeChange,
  onExpectedDateTimeChange,
  showExpectedDate = true,
  disabled = false,
  beginPlaceholder = 'Selecione a data inicial',
  expectedPlaceholder = 'Selecione a data de vencimento',
  minBeginDate,
  minExpectedDate,
}: TaskDateTimeFieldsProps) {
  const beginParsed = useMemo(() => parseTaskDateTime(beginDateTime), [beginDateTime])
  const expectedParsed = useMemo(() => parseTaskDateTime(expectedDateTime), [expectedDateTime])
  const useExpandedLayout = Boolean(beginParsed.date || expectedParsed.date)

  const handleBeginDateChange = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime())) {
      onBeginDateTimeChange(null)
      return
    }

    const dateValue = dayjs(date).format('YYYY-MM-DD')
    onBeginDateTimeChange(buildTaskDateTime(dateValue, beginParsed.time, 'begin'))
  }

  const handleBeginTimeChange = (time: string | null) => {
    if (!beginParsed.date) {
      return
    }

    onBeginDateTimeChange(buildTaskDateTime(beginParsed.date, time, 'begin'))
  }

  const handleExpectedDateChange = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime())) {
      onExpectedDateTimeChange(null)
      return
    }

    const dateValue = dayjs(date).format('YYYY-MM-DD')
    onExpectedDateTimeChange(buildTaskDateTime(dateValue, expectedParsed.time, 'expected'))
  }

  const handleExpectedTimeChange = (time: string | null) => {
    if (!expectedParsed.date) {
      return
    }

    onExpectedDateTimeChange(buildTaskDateTime(expectedParsed.date, time, 'expected'))
  }

  const beginDatePicker = (
    <DatePicker
      selected={toDatePickerValue(beginDateTime)}
      onChange={handleBeginDateChange}
      isClearable={!disabled}
      disabled={disabled}
      placeholderText={beginPlaceholder}
      className='form-control form-control-solid py-2 rounded fw-normal w-100'
      dateFormat='dd/MM/yyyy'
      minDate={minBeginDate}
      showYearDropdown
      showMonthDropdown
      dropdownMode='select'
      locale='pt-BR'
      popperClassName='react-datepicker-popper-custom'
    />
  )

  const expectedDatePicker = (
    <DatePicker
      selected={toDatePickerValue(expectedDateTime)}
      onChange={handleExpectedDateChange}
      isClearable={!disabled}
      disabled={disabled}
      placeholderText={expectedPlaceholder}
      className='form-control form-control-solid py-2 rounded fw-normal w-100'
      dateFormat='dd/MM/yyyy'
      minDate={minExpectedDate}
      showYearDropdown
      showMonthDropdown
      dropdownMode='select'
      locale='pt-BR'
      popperClassName='react-datepicker-popper-custom'
    />
  )

  if (!useExpandedLayout) {
    return (
      <div className='row g-3 task-datetime-fields'>
        <div className='col-6'>
          <div className='w-100'>{beginDatePicker}</div>
        </div>
        {showExpectedDate ? (
          <div className='col-6'>
            <div className='w-100'>{expectedDatePicker}</div>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-3 task-datetime-fields'>
      <div className='row g-3'>
        <div className='col-6'>
          <div className='w-100'>{beginDatePicker}</div>
        </div>
        <div className='col-6'>
          {beginParsed.date ? (
            <TaskTimeInput24h
              value={beginParsed.time}
              onChange={handleBeginTimeChange}
              disabled={disabled}
            />
          ) : null}
        </div>
      </div>

      {showExpectedDate ? (
        <div className='row g-3'>
          <div className='col-6'>
            <div className='w-100'>{expectedDatePicker}</div>
          </div>
          <div className='col-6'>
            {expectedParsed.date ? (
              <TaskTimeInput24h
                value={expectedParsed.time}
                onChange={handleExpectedTimeChange}
                disabled={disabled}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}
